.focus,
.unavailable {
  min-width: 0;
  color: var(--color-text-body);
}

.focus {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-height: 64px;
  padding-block-end: var(--space-2);
  border-block-end: 1px solid var(--color-hairline);
}

.header h1,
.header p,
.newThreadIntro h2,
.drawerHeader h2,
.historyPanelHeader strong,
.historyPanelHeader span {
  margin: 0;
}

.header h1 {
  max-width: 760px;
  color: var(--color-text-primary);
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.15;
  letter-spacing: -0.035em;
}

.eyebrow,
.messageLabel {
  color: var(--color-text-tertiary);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.085em;
  text-transform: uppercase;
}

.headerActions,
.compactActions,
.reviewActions,
.composerFooter,
.runFeedback,
.drawerHeader {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.headerActions button,
.secondaryButton,
.historyCollapseButton,
.mobileHistoryButton,
.compactActions button,
.reviewActions button,
.runFeedback button,
.loadOlder,
.loadMore,
.jumpLatest,
.drawerHeader button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: var(--target-min);
  padding: 0 var(--space-2);
  border: 1px solid var(--color-hairline);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font: inherit;
  font-weight: 650;
  cursor: pointer;
}

.headerActions button:hover,
.compactActions button:hover,
.reviewActions button:hover,
.runFeedback button:hover,
.loadOlder:hover,
.loadMore:hover,
.jumpLatest:hover,
.drawerHeader button:hover {
  border-color: var(--color-boundary);
  background: var(--color-surface-raised);
}

button:disabled {
  color: var(--color-text-tertiary);
  cursor: not-allowed;
  opacity: 0.62;
}

.headerActions .mobileHistoryButton {
  display: none;
}

.layout {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: 292px minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
  transition: grid-template-columns var(--motion-duration-disclosure) var(--motion-easing-standard);
}

.layout[data-history-collapsed="true"] {
  grid-template-columns: minmax(0, 1fr);
}

.desktopHistory {
  min-width: 0;
  overflow: hidden;
  border-inline-end: 1px solid var(--color-hairline);
  background: var(--color-surface-raised);
}

.desktopHistory[hidden] {
  display: none;
}

.historyPanel {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 292px;
  height: 100%;
  min-height: 0;
  padding: var(--space-2);
}

.historyPanelHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1);
  min-height: 32px;
  color: var(--color-text-primary);
}

.historyPanelHeader span {
  display: grid;
  place-items: center;
  min-width: 28px;
  height: 28px;
  padding-inline: 7px;
  border-radius: 999px;
  background: var(--color-recessed);
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.historySearch {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px;
}

.historySearch label,
.historyResults,
.composer,
.composerField,
.contextChoices,
.queryReview,
.queryReview label,
.proposalReview > div {
  display: grid;
  gap: var(--space-1);
}

.historySearch input,
.queryReview input,
.branchSelector select {
  width: 100%;
  min-height: var(--target-min);
  padding-inline: 12px;
  border: 1px solid var(--color-boundary);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text-primary);
}

.historySearch input::placeholder,
.composer textarea::placeholder {
  color: var(--color-text-tertiary);
}

.compactActions button[type="submit"] {
  min-width: var(--target-min);
  padding: 0;
}

.historyResults {
  flex: 1 1 auto;
  align-content: start;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.historyItem {
  display: grid;
  gap: 4px;
  width: 100%;
  min-height: 56px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--color-text-body);
  text-align: start;
  cursor: pointer;
}

.historyItem:hover {
  border-color: var(--color-hairline);
  background: var(--color-surface);
}

.historyItem strong {
  color: var(--color-text-primary);
  overflow-wrap: anywhere;
}

.historyItem time {
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
}

.loadMore {
  width: 100%;
}

.threadSurface {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.newThread {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-3);
  width: min(720px, 100%);
  min-height: 0;
  margin-inline: auto;
  padding: var(--space-4) var(--space-3);
  overflow-y: auto;
}

.newThreadIntro {
  text-align: center;
}

.newThreadIntro h2 {
  max-width: 28ch;
  margin-inline: auto;
  color: var(--color-text-primary);
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  line-height: 1.2;
  letter-spacing: -0.025em;
}
.newThreadIntro > p {
  max-width: 52ch;
  margin: var(--space-1) auto 0;
  color: var(--color-text-secondary);
}

.starterActions {
  display: grid;
  gap: var(--space-1);
  margin-block-start: var(--space-3);
}

.starterButtons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.starterButtons button,
.messageActions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: var(--target-min);
  padding: 0 var(--space-2);
  border: 1px solid var(--color-hairline);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font: inherit;
  font-weight: 650;
  cursor: pointer;
}


.branchSelector {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: auto minmax(180px, 260px);
  align-items: center;
  gap: var(--space-1);
  width: min(780px, calc(100% - (var(--space-3) * 2)));
  margin: var(--space-2) auto 0;
}

.branchSelector > span,
.queryReview label > span {
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  font-weight: 650;
}

.messageScroller {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--space-3) max(var(--space-3), calc((100% - 780px) / 2));
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-block: var(--space-3) 220px;
}

.loadOlder {
  display: flex;
  margin: 0 auto var(--space-3);
}

.assistantMessage,
.userMessage,
.runEvents,
.queryReview,
.proposalReview,
.runFeedback,
.inlineState,
.inlineNotice,
.error {
  width: min(780px, 100%);
  margin-inline: auto;
}

.assistantMessage,
.userMessage {
  margin-block-end: var(--space-3);
}

.assistantMessage {
  color: var(--color-text-body);
}



.userMessage {
  width: min(680px, 88%);
  margin-inline-end: 0;
  padding: 14px var(--space-2);
  border-radius: var(--radius-surface) var(--radius-surface) 4px var(--radius-surface);
  background: var(--color-accent-subtle);
  color: var(--color-text-primary);
}

.messageLabel {
  margin: 0 0 var(--space-1);
}
.messageActions {
  display: flex;
  justify-content: flex-start;
  margin-block-start: var(--space-1);
}

.pendingTurn {
  margin: var(--space-1) 0 0;
  color: var(--color-text-tertiary);
  font-size: 0.8125rem;
}

.documentBody {
  font-size: 1rem;
  line-height: 1.72;
  overflow-wrap: anywhere;
}

.documentBody p {
  margin: 0 0 var(--space-2);
  white-space: pre-wrap;
}

.documentBody p:last-child {
  margin-block-end: 0;
}

.documentBody > :first-child {
  margin-block-start: 0;
}

.documentBody > :last-child {
  margin-block-end: 0;
}

.documentBody h1,
.documentBody h2,
.documentBody h3 {
  margin: var(--space-3) 0 var(--space-1);
  color: var(--color-text-primary);
  font-size: 1em;
  line-height: 1.4;
}

.documentBody ul,
.documentBody ol,
.documentBody blockquote,
.documentBody pre,
.documentBody table {
  margin: 0 0 var(--space-2);
}

.documentBody ul,
.documentBody ol {
  padding-inline-start: var(--space-3);
}

.documentBody blockquote {
  padding-inline-start: var(--space-2);
  border-inline-start: 3px solid var(--color-boundary);
  color: var(--color-text-secondary);
}

.documentBody pre {
  padding: var(--space-2);
  border-radius: var(--radius-control);
  background: var(--color-recessed);
  overflow-x: auto;
}

.documentBody code {
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.9em;
}

.documentBody a {
  color: var(--color-accent);
  text-underline-offset: 3px;
}

.documentBody table {
  display: block;
  max-width: 100%;
  border-collapse: collapse;
  overflow-x: auto;
}

.documentBody th,
.documentBody td {
  padding: 6px 8px;
  border: 1px solid var(--color-hairline);
  text-align: start;
}

.runEvents:empty {
  display: none;
}

.citations {
  margin-block: var(--space-3);
  padding-block-start: var(--space-2);
  border-block-start: 1px solid var(--color-hairline);
}

.citations h3 {
  margin: 0 0 var(--space-1);
  font-size: 0.875rem;
}

.citations ol {
  display: grid;
  gap: var(--space-1);
  margin: 0;
  padding-inline-start: var(--space-3);
}

.citations a {
  min-height: auto;
  color: var(--color-accent);
  overflow-wrap: anywhere;
  text-underline-offset: 3px;
}

.queryReview,
.proposalReview {
  margin-block: var(--space-3);
  padding: var(--space-2);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-surface);
  background: var(--color-warning-subtle);
}

.queryReview h3,
.queryReview p,
.proposalReview p,
.proposalReview dl {
  margin: 0;
}

.reviewActions {
  flex-wrap: wrap;
  margin-block-start: var(--space-1);
}

.proposalReview summary {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: var(--target-min);
  cursor: pointer;
  color: var(--color-text-primary);
  font-weight: 650;
  list-style: none;
}

.proposalReview summary::-webkit-details-marker {
  display: none;
}

.proposalReview dl div {
  display: grid;
  grid-template-columns: minmax(100px, 0.35fr) 1fr;
  gap: var(--space-1);
}

.proposalReview dt {
  color: var(--color-text-secondary);
}

.proposalReview dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.runFeedback {
  justify-content: flex-start;
  min-height: var(--target-min);
  margin-block: var(--space-1) var(--space-2);
  color: var(--color-text-tertiary);
  font-size: 0.8125rem;
}


.runPhase {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.waitingDot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--color-accent);
  opacity: 0.42;
}

.runFeedback[data-animating="true"] .waitingDot {
  animation: focus-waiting-dot 1.4s ease-in-out infinite;
}



@keyframes focus-waiting-dot {
  50% {
    opacity: 0.9;
    transform: scale(1.2);
  }
}

.jumpLatest {
  position: sticky;
  inset-block-end: var(--space-1);
  display: flex;
  margin-inline: auto;
  box-shadow: var(--shadow-surface);
}

.composer {
  position: sticky;
  z-index: 2;
  inset-block-end: 0;
  flex: 0 0 auto;
  width: min(720px, calc(100% - (var(--space-3) * 2)));
  margin: 0 auto var(--space-2);
  padding: 8px;
  border: 1px solid var(--color-boundary);
  border-radius: 20px;
  background: var(--color-surface);
  box-shadow: var(--shadow-surface);
}

.composer:focus-within {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.newThread .composer {
  position: relative;
  inset: auto;
  width: 100%;
  margin: 0;
}

.contextChips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 2px 4px 6px;
}

.contextChip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-hairline);
  border-radius: 999px;
  background: var(--color-surface-raised);
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
}

.contextChip:hover {
  border-color: var(--color-boundary);
  color: var(--color-text-primary);
}

.contextChip span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.composer textarea {
  width: 100%;
  min-height: 60px;
  max-height: 200px;
  padding: 10px 8px;
  border: 0;
  border-radius: 12px;
  outline: 0;
  background: transparent;
  color: var(--color-text-primary);
  font: inherit;
  font-size: 1rem;
  line-height: 1.5;
  resize: none;
}

.composer textarea:focus-visible {
  outline: 0;
  box-shadow: none;
}
.composerNotice {
  margin: 0;
  padding: 0 8px 6px;
  color: var(--color-text-tertiary);
  font-size: 0.8125rem;
}


.composerFooter,
.composerTools {
  display: flex;
  align-items: center;
}

.composerFooter {
  justify-content: space-between;
  gap: var(--space-1);
}

.composerTools {
  gap: 4px;
}

.contextPicker {
  position: relative;
}

.toolButton,
.primaryButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--target-min);
  min-width: var(--target-min);
  min-height: var(--target-min);
  padding: 0;
  border-radius: 999px;
  font: inherit;
  cursor: pointer;
}

.toolButton {
  border: 0;
  background: transparent;
  color: var(--color-text-secondary);
}

.toolButton:hover,
.toolButton[aria-pressed="true"] {
  background: var(--color-recessed);
  color: var(--color-text-primary);
}
.publicSearchButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--target-min);
  padding: 0 10px;
  border: 0;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 650;
  cursor: pointer;
}

.publicSearchButton:hover,
.publicSearchButton[aria-pressed="true"] {
  background: var(--color-recessed);
  color: var(--color-text-primary);
}

.publicSearchDisclosure {
  max-width: 34ch;
  margin: 0;
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
  line-height: 1.35;
}

.contextPanel {
  position: absolute;
  z-index: 4;
  inset-block-end: calc(100% + 8px);
  inset-inline-start: 0;
  width: min(360px, calc(100vw - (var(--space-3) * 2)));
  padding: 8px;
  border: 1px solid var(--color-boundary);
  border-radius: var(--radius-surface);
  background: var(--color-surface);
  box-shadow: var(--shadow-overlay);
}

.contextPanel[hidden] {
  display: none;
}
.contextUpload,
.contextOpenDocuments {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: var(--target-min);
  padding: 7px 9px;
  border: 1px solid var(--color-hairline);
  border-radius: var(--radius-control);
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
}

.contextUpload {
  width: 100%;
  justify-content: center;
  background: var(--color-surface-raised);
}

.contextUpload:hover,
.contextOpenDocuments:hover {
  border-color: var(--color-boundary);
  color: var(--color-text-primary);
}

.contextOpenDocuments {
  justify-content: center;
  width: 100%;
  margin-top: 6px;
  background: transparent;
}

.contextChoice[data-disabled="true"] {
  cursor: not-allowed;
  opacity: 0.68;
}

.contextChoices {
  display: grid;
  gap: 4px;
  max-height: min(320px, 48dvh);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.contextChoice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: var(--target-min);
  padding: 7px 8px;
  border-radius: var(--radius-control);
  cursor: pointer;
}

.contextChoice:hover {
  background: var(--color-surface-raised);
}

.contextChoice input {
  flex: 0 0 auto;
  width: 18px;
  min-height: 18px;
  height: 18px;
  margin: 4px 0 0;
  accent-color: var(--color-accent);
}

.contextChoice span {
  display: grid;
  min-width: 0;
}

.contextChoice strong,
.contextChoice small {
  overflow-wrap: anywhere;
}

.contextChoice small {
  color: var(--color-text-tertiary);
}

.contextChoices > p {
  margin: 0;
  padding: 8px;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.contextUnavailable {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1);
  padding: 8px;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.contextUnavailable p {
  margin: 0;
}

.contextUnavailable button {
  flex: 0 0 auto;
  min-height: 36px;
  padding-inline: 12px;
  border: 1px solid var(--color-boundary);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font: inherit;
  cursor: pointer;
}

.primaryButton {
  border: 0;
  background: var(--color-accent);
  color: #fff;
}

.primaryButton:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.primaryButton:focus-visible {
  outline-color: var(--focus-inverse-outline-color);
  box-shadow: var(--focus-inverse-keyline);
}

.inlineState,
.inlineNotice,
.error {
  padding: var(--space-2);
  color: var(--color-text-secondary);
  text-align: center;
}

.inlineState {
  display: grid;
  justify-items: center;
  gap: var(--space-1);
  border: 1px solid var(--color-hairline);
  border-radius: var(--radius-control);
}

.inlineState p,
.error p {
  margin: 0;
}

.error {
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-control);
  background: var(--color-danger-subtle);
  color: var(--color-danger);
  font-weight: 700;
}

.unavailable {
  display: grid;
  place-content: center;
  min-height: 50vh;
  text-align: center;
}

.liveRegion,
.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.historyDialog {
  width: min(420px, calc(100vw - var(--space-2)));
  height: min(720px, calc(100dvh - var(--space-2)));
  max-width: none;
  max-height: none;
  margin: auto 0 auto auto;
  padding: 0;
  border: 1px solid var(--color-hairline);
  border-radius: var(--radius-overlay) 0 0 var(--radius-overlay);
  background: var(--color-surface);
  color: var(--color-text-body);
  box-shadow: var(--shadow-overlay);
}

.historyDialog::backdrop {
  background: rgb(23 26 43 / 38%);
}

.historyDialog .historyPanel {
  width: 100%;
  height: calc(100% - 68px);
}

.drawerHeader {
  justify-content: space-between;
  min-height: 68px;
  padding: var(--space-2);
  border-block-end: 1px solid var(--color-hairline);
}

@media (min-width: 901px) {
  .historyDialog {
    display: none;
  }
}

@media (max-width: 900px) {

  .header {
    min-height: 56px;
    padding-block-end: var(--space-1);
  }

  .headerActions {
    justify-content: flex-end;
  }

  .headerActions .mobileHistoryButton {
    display: inline-flex;
  }

  .headerActions .historyCollapseButton {
    display: none;
  }

  .layout,
  .layout[data-history-collapsed="true"] {
    grid-template-columns: minmax(0, 1fr);
  }

  .desktopHistory {
    display: none;
  }

  .newThread {
    width: 100%;
    justify-content: flex-start;
    padding: var(--space-3) 0 var(--space-2);
  }

  .branchSelector,
  .composer {
    width: 100%;
  }

  .messageScroller {
    padding: var(--space-2) 0;
    scroll-padding-block-end: 260px;
  }

  .composer {
    inset-block-end: 0;
    margin-block-end: 0;
    border-radius: var(--radius-surface) var(--radius-surface) 0 0;
  }

  .newThread .composer {
    border-radius: var(--radius-surface);
  }
}

@media (max-width: 620px) {
  .header h1 {
    font-size: 1.45rem;
  }

  .headerActions button {
    padding-inline: 10px;
    font-size: 0.82rem;
  }


  .branchSelector {
    grid-template-columns: minmax(0, 1fr);
  }

  .reviewActions {
    align-items: stretch;
    flex-direction: column;
  }

  .reviewActions button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .runFeedback[data-animating="true"] .waitingDot {
    animation: none;
  }
}

@media (forced-colors: active) {
  .historyItem,
  .assistantMessage,
  .userMessage,
  .composer,
  .queryReview,
  .proposalReview {
    forced-color-adjust: auto;
  }

  .composer:focus-within {
    outline-color: Highlight;
  }
}
